import uniq from 'lodash/uniq';
import Order from 'communication/types/order/Order';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { mapOrderSide, mapSymbolStreamField } from 'app/store/orders/mapping';
import { OrderSide } from 'communication/types/order/OrderSide';

export interface PortfolioSelection {
    selectedOrders: Order[];
    selectedPositions: PortfolioPosition[];
    selectedSymbolQuantities: PortfolioSymbolQuantity[];
    selectedSymbols: string[];
    symbology: SymbologyValues;
    overridingActiveInput?: boolean;
}

interface PortfolioSymbolQuantityData {
    symbol: string;
    qty: number;
    qtyRemaining: number;
}

export interface PortfolioSymbolQuantity extends PortfolioSymbolQuantityData {
    netPosition: PortfolioPosition;
}

export interface PortfolioPosition extends PortfolioSymbolQuantityData {
    side: OrderSide;
}

export const emptySelectionState: PortfolioSelection = {
    selectedOrders: [],
    selectedPositions: [],
    selectedSymbolQuantities: [],
    selectedSymbols: [],
    symbology: SymbologyValues.Instinet,
};

export const isEmptyPortfolioSelection = (s: PortfolioSelection | undefined) =>
    s === undefined ||
    (s.selectedOrders.length === 0 &&
        s.selectedPositions.length === 0 &&
        s.selectedSymbolQuantities.length === 0 &&
        s.selectedSymbols.length === 0);

export function buildPortfolioSelection(options?: {
    selectedOrders?: Order[];
    selectedPositions?: PortfolioPosition[];
    symbology: SymbologyValues;
    combineSellsAndShorts?: boolean;
    overridingActiveInput?: boolean;
}): PortfolioSelection {
    if (options && !options?.symbology) {
        throw new Error('symbology is required');
    }

    const ret: PortfolioSelection = {
        selectedOrders: [],
        selectedPositions: [],
        selectedSymbolQuantities: [],
        selectedSymbols: [],
        symbology: options?.symbology ?? SymbologyValues.Instinet,
        overridingActiveInput: options?.overridingActiveInput,
    };

    if (options && options.selectedOrders) {
        buildFromSelectedOrders(
            options.selectedOrders,
            !!options.combineSellsAndShorts,
            options.symbology
        );
    } else if (options && options.selectedPositions) {
        ret.selectedPositions = options.selectedPositions;
    }

    aggregateSymbolQuantities();
    aggregateSymbols();

    return ret;

    function aggregateSymbolQuantities() {
        const dict = ret.selectedPositions.reduce(
            (t: { [symbol: string]: PortfolioSymbolQuantity }, p) => {
                if (!t[p.symbol]) {
                    t[p.symbol] = {
                        symbol: p.symbol,
                        qty: 0,
                        qtyRemaining: 0,
                        netPosition: {
                            symbol: p.symbol,
                            qty: 0,
                            qtyRemaining: 0,
                            side: 'B',
                        },
                    };
                }
                t[p.symbol].qty = t[p.symbol].qty + getPositionQty(p);
                t[p.symbol].qtyRemaining =
                    t[p.symbol].qtyRemaining + getPositionQtyRemaining(p);
                return t;
            },
            {}
        );

        ret.selectedSymbolQuantities = Object.values(dict);
        for (const res of ret.selectedSymbolQuantities) {
            res.netPosition.qty = Math.abs(res.qty);
            res.netPosition.qtyRemaining = Math.abs(res.qtyRemaining);
            res.netPosition.side = res.qty >= 0 ? 'B' : 'S';
        }
    }

    function getPositionQty(position: PortfolioPosition) {
        return getPositionNumericField(position, 'qty');
    }

    function getPositionQtyRemaining(position: PortfolioPosition) {
        return getPositionNumericField(position, 'qtyRemaining');
    }

    function getPositionNumericField(
        position: PortfolioPosition,
        fieldName: 'qty' | 'qtyRemaining'
    ) {
        return position.side === 'B'
            ? position[fieldName]
            : -position[fieldName];
    }

    function aggregateSymbols() {
        ret.selectedSymbols = uniq(ret.selectedPositions.map((p) => p.symbol));
    }

    function buildFromSelectedOrders(
        selectedOrders: Order[],
        combineSellsAndShorts: boolean,
        symbology: SymbologyValues
    ): void {
        const symbolField = mapSymbolStreamField(symbology);

        ret.selectedOrders = selectedOrders;

        const positions = ret.selectedOrders.map<PortfolioPosition>((o) => ({
            symbol: o[symbolField] as string,
            side: mapOrderSide(o.side, combineSellsAndShorts),
            qty: o.qty,
            qtyRemaining: o.rem!,
        }));

        const toKey = (position: PortfolioPosition) =>
            position.symbol + position.side;
        const accum: { [key: string]: PortfolioPosition } = {};
        positions.forEach((position) => {
            const key = toKey(position);
            if (accum[key]) {
                accum[key].qty = accum[key].qty + position.qty;
                if (position.qtyRemaining) {
                    accum[key].qtyRemaining =
                        (accum[key].qtyRemaining ?? 0) + position.qtyRemaining;
                }
            } else {
                accum[key] = { ...position };
            }
        });

        ret.selectedPositions = Object.values(accum);
    }
}
